import { db, auth } from '../firebase/config';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  onSnapshot 
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

import app from '../firebase/config';

const functions = getFunctions(app, 'us-central1');

class TelehealthService {
  /**
   * Check if user is authenticated and refresh token if needed
   */
  static async ensureAuthenticated() {
    console.log('ensureAuthenticated: Checking current user...');
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      console.error('ensureAuthenticated: No current user found');
      throw new Error('User must be authenticated');
    }
    
    console.log('ensureAuthenticated: User found:', {
      uid: currentUser.uid,
      email: currentUser.email,
      emailVerified: currentUser.emailVerified
    });
    
    // Just return the user, don't call getIdToken here to avoid double calls
    return currentUser;
  }

  /**
   * Create a new telehealth session using Firebase Functions
   */
  static async createSession(sessionData) {
    console.log('=== createSession called ===');
    console.log('sessionData:', sessionData);
    
    try {
      // Ensure user is authenticated
      console.log('Step 1: Ensuring authentication...');
      const currentUser = await this.ensureAuthenticated();
      
      console.log('Step 2: User authenticated:', {
        uid: currentUser.uid,
        email: currentUser.email
      });
      
      console.log('Step 3: Getting ID token...');
      let idToken;
      try {
        idToken = await currentUser.getIdToken();
        console.log('Step 4: ID token obtained, length:', idToken.length);
      } catch (tokenError) {
        console.error('Step 4 ERROR: Failed to get ID token:', tokenError);
        console.error('Token error type:', tokenError.constructor.name);
        console.error('Token error message:', tokenError.message);
        throw new Error('Failed to get authentication token. Please refresh the page and try again.');
      }
      
      console.log('Step 5: Making HTTP request...');
      const response = await fetch('https://quantumleap-emr-dev.web.app/createTelehealthSession', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(sessionData)
      });
      
      console.log('Step 6: Response received, status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('HTTP Error Response:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      console.log('Step 7: Parsing JSON response...');
      const responseText = await response.text();
      console.log('Step 7.5: Raw response text:', responseText);
      
      let result;
      try {
        result = JSON.parse(responseText);
        console.log('Step 8: Parsed response:', result);
      } catch (parseError) {
        console.error('Step 8 ERROR: Failed to parse JSON:', parseError);
        console.error('Raw response was:', responseText);
        throw new Error('Invalid response format from server');
      }
      
      if (result.success) {
        const sessionResult = {
          id: result.sessionId,
          ...sessionData,
          status: 'scheduled',
          createdAt: new Date(),
          updatedAt: new Date()
        };
        console.log('Step 9: Returning session result:', sessionResult);
        return sessionResult;
      } else {
        throw new Error('Failed to create session');
      }
    } catch (error) {
      console.error('=== Error in createSession ===');
      console.error('Error type:', error.constructor.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      
      // Provide more specific error messages
      if (error.message?.includes('HTTP 401')) {
        throw new Error('Authentication failed. Please refresh the page and try again.');
      } else if (error.message?.includes('HTTP 500')) {
        throw new Error('Backend service is temporarily unavailable. Please try again in a few moments.');
      } else if (error.message?.includes('CORS')) {
        throw new Error('Network error. Please check your connection and try again.');
      } else {
        throw error;
      }
    }
  }

  /**
   * Get session by ID
   */
  static async getSession(sessionId) {
    const docRef = doc(db, 'telehealth-sessions', sessionId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      };
    } else {
      throw new Error('Session not found');
    }
  }

  /**
   * Update session status
   */
  static async updateSessionStatus(sessionId, status, additionalData = {}) {
    const docRef = doc(db, 'telehealth-sessions', sessionId);
    await updateDoc(docRef, {
      status,
      updatedAt: serverTimestamp(),
      ...additionalData
    });
    // Update encounter if session is linked
    if (additionalData.encounterId) {
      const encounterRef = doc(db, 'encounters', additionalData.encounterId);
      await updateDoc(encounterRef, {
        'telehealthDetails.sessionId': sessionId,
        'telehealthDetails.status': status,
        updatedAt: serverTimestamp()
      });
    }
    return { success: true };
  }

    /**
   * Join session using Firebase Functions
   */
  static async joinSession(sessionId, userId, userRole) {
    console.log('TelehealthService.joinSession called with:', { sessionId, userId, userRole });
    
    try {
      // Validate required parameters
      if (!sessionId || !userId || !userRole) {
        throw new Error('Missing required parameters: sessionId, userId, or userRole');
      }
      
      // Ensure user is authenticated
      const currentUser = await this.ensureAuthenticated();
      
      console.log('Current authenticated user:', {
        uid: currentUser.uid,
        email: currentUser.email,
        emailVerified: currentUser.emailVerified
      });
      
      // Get the current token for debugging
      const token = await currentUser.getIdToken();
      console.log('Current auth token (first 50 chars):', token.substring(0, 50) + '...');
      
      const joinSessionFunction = httpsCallable(functions, 'joinTelehealthSession');
      const result = await joinSessionFunction({
        sessionId,
        userRole
      });
      
      console.log('joinSession result:', result);
      
      if (result.data.success) {
        return { success: true };
      } else {
        throw new Error('Failed to join session');
      }
    } catch (error) {
      console.error('Error in joinSession:', error);
      throw error;
    }
  }

  /**
   * Leave session using Firebase Functions
   */
  static async leaveSession(sessionId, userId) {
    const leaveSessionFunction = httpsCallable(functions, 'leaveTelehealthSession');
    const result = await leaveSessionFunction({ sessionId });
    if (result.data.success) {
      return { success: true };
    } else {
      throw new Error('Failed to leave session');
    }
  }

  /**
   * Get sessions for user (currently returns empty array due to security rules)
   */
  static async getUserSessions(userId, status = null) {
    // Placeholder for future implementation
    return [];
  }

  /**
   * Get session by encounter ID
   */
  static async getSessionByEncounterId(encounterId) {
    const encounterRef = doc(db, 'encounters', encounterId);
    const encounterSnap = await getDoc(encounterRef);
    if (!encounterSnap.exists()) return null;
    const encounterData = encounterSnap.data();
    const sessionId = encounterData?.telehealthDetails?.sessionId;
    if (!sessionId) return null;
    const sessionRef = doc(db, 'telehealth-sessions', sessionId);
    const sessionSnap = await getDoc(sessionRef);
    if (!sessionSnap.exists()) return null;
    return {
      id: sessionSnap.id,
      ...sessionSnap.data()
    };
  }

  /**
   * Record session metrics
   */
  static async recordSessionMetrics(sessionId, metrics) {
    const metricsRef = collection(db, 'telehealth-sessions', sessionId, 'metrics');
    await addDoc(metricsRef, {
      ...metrics,
      timestamp: serverTimestamp()
    });
  }

  /**
   * Get session metrics
   */
  static async getSessionMetrics(sessionId) {
    const metricsRef = collection(db, 'telehealth-sessions', sessionId, 'metrics');
    const snapshot = await getDocs(metricsRef);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }

  /**
   * Send chat message using Firebase Functions
   */
  static async sendChatMessage(sessionId, message) {
    const sendMessageFunction = httpsCallable(functions, 'sendTelehealthChatMessage');
    const result = await sendMessageFunction({
      sessionId,
      message
    });
    return result.data.success;
  }

  /**
   * Get chat messages
   */
  static async getChatMessages(sessionId) {
    const chatRef = collection(db, 'telehealth-sessions', sessionId, 'chat');
    const q = query(chatRef, orderBy('timestamp', 'asc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }

  /**
   * Listen to chat messages in real-time
   */
  static listenToChat(sessionId, callback) {
    const chatRef = collection(db, 'telehealth-sessions', sessionId, 'chat');
    const q = query(chatRef, orderBy('timestamp', 'asc'));
    return onSnapshot(q, (snapshot) => {
      const messages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      callback(messages);
    });
  }

  /**
   * Update quality metrics using Firebase Functions
   */
  static async updateQualityMetrics(sessionId, userId, metrics) {
    const updateMetricsFunction = httpsCallable(functions, 'updateSessionQualityMetrics');
    const result = await updateMetricsFunction({
      sessionId,
      metrics: {
        ...metrics,
        userId,
        timestamp: Date.now()
      }
    });
    return result.data.success;
  }

  /**
   * End session using Firebase Functions
   */
  static async endSession(sessionId, endData = {}) {
    const endSessionFunction = httpsCallable(functions, 'endTelehealthSession');
    const result = await endSessionFunction({
      sessionId,
      reason: endData.reason || 'provider_ended',
      qualityReport: endData.qualityReport || {}
    });
    if (result.data.success) {
      return { success: true };
    } else {
      throw new Error('Failed to end session');
    }
  }

  /**
   * Get session analytics using Firebase Functions
   */
  static async getSessionAnalytics(sessionId) {
    const getAnalyticsFunction = httpsCallable(functions, 'getSessionAnalytics');
    const result = await getAnalyticsFunction({ sessionId });
    return result.data;
  }

  /**
   * Validate session access
   */
  static async validateSessionAccess(sessionId, userId) {
    const session = await this.getSession(sessionId);
    const isAuthorized = session.participants?.some(p => p.id === userId) || 
                        session.providerId === userId || 
                        session.patientId === userId;
    return {
      authorized: isAuthorized,
      session: isAuthorized ? session : null
    };
  }

  /**
   * Get sessions for an encounter
   */
  static async getSessionsForEncounter(encounterId) {
    const sessionsQuery = query(
      collection(db, 'telehealth-sessions'),
      where('encounterId', '==', encounterId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(sessionsQuery);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }

  /**
   * Get active sessions for a user
   */
  static async getActiveSessionsForUser(userId) {
    const sessionsQuery = query(
      collection(db, 'telehealth-sessions'),
      where('status', '==', 'active'),
      where('participants', 'array-contains', userId)
    );
    const snapshot = await getDocs(sessionsQuery);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }

  /**
   * Listen to session changes
   */
  static subscribeToSession(sessionId, callback) {
    const sessionRef = doc(db, 'telehealth-sessions', sessionId);
    return onSnapshot(sessionRef, (doc) => {
      if (doc.exists()) {
        callback({
          id: doc.id,
          ...doc.data()
        });
      } else {
        callback(null);
      }
    });
  }

  /**
   * Get a Twilio Video access token for a session and user
   */
  static async getTwilioVideoToken(sessionId, identity) {
    try {
      // Ensure user is authenticated
      await this.ensureAuthenticated();
      
      const getTokenFunction = httpsCallable(functions, 'getTwilioVideoToken');
      const result = await getTokenFunction({ sessionId, identity });
      return result.data.token;
    } catch (error) {
      console.error('Error getting Twilio video token:', error);
      throw error;
    }
  }
  
  /**
   * Check if user needs to be redirected to login
   */
  static async checkAuthenticationStatus() {
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      return { authenticated: false, needsLogin: true };
    }
    
    try {
      await currentUser.getIdToken(true);
      return { authenticated: true, needsLogin: false, user: currentUser };
    } catch (error) {
      console.error('Authentication check failed:', error);
      return { authenticated: false, needsLogin: true, error };
    }
  }
  
  /**
   * Wait for authentication to be ready
   */
  static async waitForAuthentication(timeoutMs = 10000) {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Authentication timeout'));
      }, timeoutMs);
      
      const unsubscribe = auth.onAuthStateChanged((user) => {
        clearTimeout(timeout);
        unsubscribe();
        
        if (user) {
          resolve(user);
        } else {
          reject(new Error('No authenticated user'));
        }
      });
    });
  }
  
  /**
   * Test authentication with Firebase Functions
   */
  static async testAuthentication() {
    try {
      const currentUser = await this.ensureAuthenticated();
      console.log('Testing authentication with user:', currentUser.uid);
      
      // Get the current token and log it
      const token = await currentUser.getIdToken();
      console.log('Current auth token (first 50 chars):', token.substring(0, 50) + '...');
      console.log('Token length:', token.length);
      
      // Check if token is valid
      const decodedToken = await currentUser.getIdTokenResult();
      console.log('Token details:', {
        authTime: decodedToken.authTime,
        issuedAtTime: decodedToken.issuedAtTime,
        expirationTime: decodedToken.expirationTime,
        claims: decodedToken.claims
      });
      
      const testFunction = httpsCallable(functions, 'testAuthentication');
      const result = await testFunction({});
      
      console.log('Authentication test result:', result.data);
      return result.data;
    } catch (error) {
      console.error('Authentication test failed:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        details: error.details,
        stack: error.stack
      });
      throw error;
    }
  }
}

export default TelehealthService; 